import React, { useEffect, useMemo, useState } from "react";
import { ContactInfo } from "../../Utils/Data";
import { useI18n } from "../../i18n/I18nContext";
import { useTranslatedCopy } from "../../i18n/useTranslatedCopy";

function buildWhatsAppUrl(phone, message) {
  const digits = String(phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export default function WhatsAppChat() {
  const notificationDelayMs = 6000;
  const promptDelayMs = 9500;
  const { locale, isRtl } = useI18n();
  const [open, setOpen] = useState(false);
  const [messageStage, setMessageStage] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const copy = useTranslatedCopy(
    {
      welcome: "Welcome to QTECH",
      prompt: "Have a question? Ask the QTECH team directly on WhatsApp.",
      message: "Hello, I would like to ask about QTECH services.",
      send: "Send message",
      header: "QTECH WhatsApp",
      aria: "Open WhatsApp chat",
    },
    locale,
    "whatsapp-chat-ui"
  );

  useEffect(() => {
    if (!open) return;
    setUnreadCount(0);
  }, [open]);

  useEffect(() => {
    let audioContextRef = null;

    const ensureAudioContext = async () => {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return null;

        if (!audioContextRef) {
          audioContextRef = new AudioContextClass();
        }

        if (audioContextRef.state === "suspended") {
          await audioContextRef.resume();
        }

        return audioContextRef;
      } catch {
        return null;
      }
    };

    const unlockAudio = async () => {
      const context = await ensureAudioContext();
      if (context) return;
    };

    const unlockEvents = ["pointerdown", "mousedown", "touchstart", "keydown"];
    unlockEvents.forEach((eventName) => {
      window.addEventListener(eventName, unlockAudio, { passive: true });
    });

    const playNotificationSound = async () => {
      try {
        const audioContext = await ensureAudioContext();
        if (!audioContext || audioContext.state !== "running") return;

        const playTone = (startOffset, frequency, volume, duration, type = "sine") => {
          const oscillator = audioContext.createOscillator();
          const gain = audioContext.createGain();
          const startTime = audioContext.currentTime + startOffset;
          const endTime = startTime + duration;

          oscillator.type = type;
          oscillator.frequency.setValueAtTime(frequency, startTime);
          oscillator.connect(gain);
          gain.connect(audioContext.destination);

          gain.gain.setValueAtTime(0.0001, startTime);
          gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

          oscillator.start(startTime);
          oscillator.stop(endTime);
        };

        playTone(0, 740, 0.16, 0.16, "sine");
        playTone(0.18, 988, 0.22, 0.26, "triangle");
      } catch {
        /* browser may block autoplayed audio */
      }
    };

    const welcomeTimer = window.setTimeout(() => {
      setMessageStage(1);
      setUnreadCount(1);
      playNotificationSound();
    }, notificationDelayMs);
    const promptTimer = window.setTimeout(() => {
      setMessageStage(2);
      setUnreadCount(2);
      playNotificationSound();
    }, promptDelayMs);

    return () => {
      window.clearTimeout(welcomeTimer);
      window.clearTimeout(promptTimer);
      unlockEvents.forEach((eventName) => {
        window.removeEventListener(eventName, unlockAudio);
      });
    };
  }, [notificationDelayMs, promptDelayMs]);

  const whatsappUrl = buildWhatsAppUrl(ContactInfo.contact_phone, copy.message);

  return (
    <div className={`joinchat joinchat--right ${isRtl ? "joinchat--rtl" : ""}`} aria-hidden="false">
      {open && messageStage > 0 ? (
        <div className="joinchat__panel">
          <div className="joinchat__panel-header">
            <strong>{copy.header}</strong>
            <button type="button" className="joinchat__close" onClick={() => setOpen(false)} aria-label="Close">
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="joinchat__panel-body">
            {messageStage >= 1 ? (
              <div className="joinchat__message joinchat__message--welcome">{copy.welcome}</div>
            ) : null}
            {messageStage >= 2 ? (
              <div className="joinchat__message joinchat__message--prompt">{copy.prompt}</div>
            ) : null}
          </div>
          <a className="joinchat__send" href={whatsappUrl} target="_blank" rel="noreferrer">
            {copy.send}
          </a>
        </div>
      ) : null}
      <button
        type="button"
        className="joinchat__button"
        aria-label={copy.aria}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <i className="fab fa-whatsapp" aria-hidden="true" />
      </button>
      {unreadCount > 0 ? <div className="joinchat__badge">{unreadCount}</div> : null}
    </div>
  );
}
